import React, { useState } from 'react';
import { CheckCircleIcon, AlertCircleIcon, AlertTriangleIcon, HelpCircleIcon, FilterIcon } from 'lucide-react';
import { Source, UnitUpload } from './supplyClassesData';
interface ExcelSheetsBlockProps {
  excelSource?: Source;
}
export const ExcelSheetsBlock = ({
  excelSource
}: ExcelSheetsBlockProps) => {
  const [filter, setFilter] = useState<string>('all');
  if (!excelSource || !excelSource.unitUploads) {
    return <div className="text-gray-500 text-sm" data-id="element-23">No Excel uploads available</div>;
  }
  // Group uploads by battalion
  const groupedUploads: Record<string, UnitUpload[]> = {};
  excelSource.unitUploads.forEach(upload => {
    if (!groupedUploads[upload.battalion]) {
      groupedUploads[upload.battalion] = [];
    }
    groupedUploads[upload.battalion].push(upload);
  });
  // Filter uploads by status
  const filteredUploads = Object.entries(groupedUploads).map(([battalion, uploads]) => {
    return {
      battalion,
      uploads: filter === 'all' ? uploads : uploads.filter(upload => upload.status === filter)
    };
  }).filter(group => group.uploads.length > 0);
  // Status icon based on status
  const StatusIcon = ({
    status
  }: {
    status: string;
  }) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="text-green-500" size={16} data-id="element-24" />;
      case 'error':
        return <AlertCircleIcon className="text-red-500" size={16} data-id="element-25" />;
      case 'warning':
        return <AlertTriangleIcon className="text-amber-500" size={16} data-id="element-26" />;
      default:
        return <HelpCircleIcon className="text-gray-400" size={16} data-id="element-27" />;
    }
  };
  // Status text for display
  const statusText = {
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    pending: 'Pending'
  };
  return <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-28">
      <div className="bg-gray-50 p-3 flex items-center justify-between" data-id="element-29">
        <div className="flex items-center" data-id="element-30">
          <span className="font-medium text-gray-700" data-id="element-31">LogStat Reports</span>
          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full" data-id="element-32">
            {excelSource.unitUploads.length} files
          </span>
        </div>
        <div className="flex items-center space-x-2" data-id="element-33">
          <FilterIcon size={14} className="text-gray-500" data-id="element-34" />
          <select value={filter} onChange={e => setFilter(e.target.value)} className="text-xs border-gray-300 rounded" data-id="element-35">
            <option value="all" data-id="element-36">All Status</option>
            <option value="success" data-id="element-37">Success</option>
            <option value="warning" data-id="element-38">Warning</option>
            <option value="error" data-id="element-39">Error</option>
            <option value="pending" data-id="element-40">Pending</option>
          </select>
        </div>
      </div>
      <div className="divide-y divide-gray-200" data-id="element-41">
        {filteredUploads.length === 0 ? <div className="p-4 text-center text-gray-500 text-sm" data-id="element-42">
            No uploads match the selected filter
          </div> : filteredUploads.map(({
        battalion,
        uploads
      }) => <div key={battalion} className="p-0" data-id="element-43">
              <div className="bg-gray-50 px-4 py-2 font-medium text-sm text-gray-700" data-id="element-44">
                {battalion}
              </div>
              <table className="min-w-full divide-y divide-gray-200" data-id="element-45">
                <thead className="bg-gray-50" data-id="element-46">
                  <tr data-id="element-47">
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-48">
                      Unit
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-49">
                      File Name
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-50">
                      Upload Time
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-51">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-52">
                  {uploads.map((upload, idx) => <tr key={idx} className={upload.status === 'error' ? 'bg-red-50' : upload.status === 'warning' ? 'bg-amber-50' : ''} data-id="element-53">
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700" data-id="element-54">
                        {upload.company}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700" data-id="element-55">
                        {upload.fileName}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-56">
                        {upload.uploadTime}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm" data-id="element-57">
                        <div className="flex items-center" data-id="element-58">
                          <StatusIcon status={upload.status} data-id="element-59" />
                          <span className="ml-1.5" data-id="element-60">
                            {statusText[upload.status]}
                            {upload.statusMessage && <span className="ml-1 text-xs text-gray-500" data-id="element-61">
                                ({upload.statusMessage})
                              </span>}
                          </span>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>)}
      </div>
    </div>;
};